import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Moon, Calendar, RefreshCw} from 'lucide-react';
import { loadEphemerisData, findClosestEphemerisEntry } from '../../astro/ephemerisUtils';

const MoonPhaseExperience = () => {
  const [page, setPage] = useState(0); // 0 = form, 1 = result
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0]
  });
  const [moonPhaseData, setMoonPhaseData] = useState(null);
  const [loading, setLoading] = useState(false);

  const handleChange = ({ target: { name, value } }) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const getDayOfWeek = (dateString) => {
    const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const date = new Date(dateString);
    return days[date.getDay()];
  };

  // Helper to normalize angle to 0-360
  const normalizeAngle = (angle) => {
    let a = angle % 360;
    if (a < 0) a += 360;
    return a;
  };

  // Get phase name/icon from phase angle
  const getPhaseInfoFromAngle = (angle) => {
    // angle: 0 = New Moon, 180 = Full Moon
    if (angle < 22.5 || angle >= 337.5) return { name: 'New Moon', icon: '🌑', description: 'A time for new beginnings and setting intentions.' };
    if (angle < 67.5) return { name: 'Waxing Crescent', icon: '🌒', description: 'A time for growth and building momentum.' };
    if (angle < 112.5) return { name: 'First Quarter', icon: '🌓', description: 'A time for action and making decisions.' };
    if (angle < 157.5) return { name: 'Waxing Gibbous', icon: '🌔', description: 'A time for refinement and preparation.' };
    if (angle < 202.5) return { name: 'Full Moon', icon: '🌕', description: 'A time for completion and celebration.' };
    if (angle < 247.5) return { name: 'Waning Gibbous', icon: '🌖', description: 'A time for gratitude and sharing.' };
    if (angle < 292.5) return { name: 'Last Quarter', icon: '🌗', description: 'A time for release and letting go.' };
    return { name: 'Waning Crescent', icon: '🌘', description: 'A time for rest and reflection.' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const selectedDate = new Date(`${formData.date}T00:00:00`);
      const year = selectedDate.getFullYear();
      const ephemerisData = await loadEphemerisData(year);
      const entry = findClosestEphemerisEntry(ephemerisData, selectedDate);
      if (!entry || !entry.planets || !entry.planets.Sun || !entry.planets.Moon) {
        throw new Error('Ephemeris data not found for this date');
      }
      const sunLon = entry.planets.Sun.longitude;
      const moonLon = entry.planets.Moon.longitude;
      const phaseAngle = normalizeAngle(moonLon - sunLon);
      const illumination = (1 - Math.cos(phaseAngle * Math.PI / 180)) / 2 * 100;
      const phaseInfo = getPhaseInfoFromAngle(phaseAngle);
      setMoonPhaseData({
        date: formData.date,
        dayOfWeek: getDayOfWeek(formData.date),
        phase: illumination,
        phaseInfo: phaseInfo
      });
      setPage(1);
    } catch (error) {
      console.error('Error calculating moon phase:', error);
      setMoonPhaseData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div 
      className="full-screen-image-container full-height margin-top-0"
      style={{
        backgroundImage: `url('/img/saturnwallpaper.jpg')`,
      }}
    >
      {page === 0 && (
        <div className="ascendant-display-container">
          <div className="ascendant-header">
            <div className="ascendant-title">
              <Moon size={24} />
              <h1>Moon Phase Calculator</h1>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="ascendant-form">
            <div className="form-section" style={{
              background: 'rgba(0, 0, 0, 0.4)',
              borderRadius: '16px',
              padding: '2rem',
              marginBottom: '2rem',
              border: '1px solid rgba(255, 215, 0, 0.2)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
            }}>
              <div className="section-title" style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
                marginBottom: '1.5rem',
                color: '#FFD700',
                fontSize: '1.2rem',
                fontWeight: '600'
              }}>
                <Calendar size={20} />
                <span>Select Date</span>
              </div>
              <div className="form-field" style={{
                marginBottom: '1rem'
              }}>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.9rem',
                  fontWeight: '500'
                }}>Choose a date to calculate the moon phase</label>
                <input
                  type="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                  style={{
                    width: '100%',
                    padding: '1rem',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 215, 0, 0.3)',
                    background: 'rgba(0, 0, 0, 0.6)',
                    color: '#fff',
                    fontSize: '1rem',
                    outline: 'none',
                    transition: 'all 0.3s ease'
                  }}
                  onFocus={(e) => {
                    e.target.style.borderColor = 'rgba(255, 215, 0, 0.6)';
                    e.target.style.boxShadow = '0 0 0 3px rgba(255, 215, 0, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                    e.target.style.boxShadow = 'none';
                  }}
                />
              </div>
              <div style={{
                textAlign: 'center',
                marginTop: '1rem',
                padding: '1rem',
                background: 'rgba(255, 215, 0, 0.1)',
                borderRadius: '8px',
                border: '1px solid rgba(255, 215, 0, 0.2)'
              }}>
                <p style={{
                  color: 'rgba(255, 255, 255, 0.8)',
                  fontSize: '0.9rem',
                  margin: 0
                }}>
                  🌙 Discover the moon's phase and illumination for any date
                </p>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="calculate-button"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Calculating...
                  </>
                ) : (
                  <>
                    <Moon size={18} />
                    Calculate Moon Phase
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {page === 1 && moonPhaseData && (
        <motion.div
          key="result"
          initial={{ opacity: 0, x: -50 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 50 }}
          transition={{ duration: 0.4 }}
          className="ascendant-display-container"
        >
          <div className="ascendant-result">
            <div className="result-header">
              <h2>Moon Phase</h2>
            </div>

            <div className="result-details">
              <div style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                margin: '1.5rem 0 2.5rem 0',
                gap: '1.5rem',
              }}>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#fff',
                  fontWeight: 500,
                }}>
                  <span style={{ opacity: 0.7 }}>Date</span> <span style={{ fontWeight: 700 }}>{moonPhaseData.date}</span>
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 215, 0, 0.4)',
                  textAlign: 'center',
                  fontSize: '1.25rem',
                  color: '#FFD700',
                  fontWeight: 700,
                  letterSpacing: '0.03em',
                }}>
                  {moonPhaseData.phaseInfo.icon} {moonPhaseData.phaseInfo.name} ({Math.min(100, Math.max(0, (moonPhaseData.phase))).toFixed(1)}%)
                </div>
                <div style={{
                  flex: 1,
                  padding: '1rem',
                  borderRadius: '12px',
                  background: 'rgba(0, 0, 0, 0.3)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  textAlign: 'center',
                  fontSize: '1.1rem',
                  color: '#fff',
                  fontWeight: 500,
                }}>
                  <span style={{ opacity: 0.7 }}>Day</span> <span style={{ fontWeight: 700 }}>{moonPhaseData.dayOfWeek}</span>
                </div>
              </div>
            </div>

            <div className="ascendant-description">
              <h3>What does this moon phase mean?</h3>
              <p>{moonPhaseData.phaseInfo.description}</p>
            </div>
          </div>

          <motion.button
            className="button is-warning"
            style={{
              position: "fixed",
              top: 100,
              left: 24,
              zIndex: 1000,
              boxShadow: "0 4px 12px rgba(0,0,0,0.3)",
              borderRadius: "12px",
              padding: "12px 16px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              fontSize: "14px",
              fontWeight: "600",
              border: "none",
              background: "linear-gradient(135deg, #ffc107 0%, #ff8f00 100%)",
              color: "#000",
              cursor: "pointer",
              transition: "all 0.3s ease"
            }}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            onClick={() => window.location.href = "/moonphase"}
            onMouseEnter={(e) => {
              e.target.style.transform = "translateY(-2px)";
              e.target.style.boxShadow = "0 6px 16px rgba(0,0,0,0.4)";
            }}
            onMouseLeave={(e) => {
              e.target.style.transform = "translateY(0)";
              e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.3)";
            }}
          >
            <ArrowLeft size={20} />
            Back
          </motion.button>
        </motion.div>
      )}
    </div>
  );
};

export default MoonPhaseExperience; 
